import { kanbanTaskPath } from 'app/routes';
import * as translations from '../strings';
import { OrderBy, UserTask, UserTaskOrder, classNames } from '../types';
import * as render from './columns';
import styles from '../index.module.css';
import { SortOrder } from 'antd/lib/table/interface';
import * as sortTask from '../sort';
import { getLangKey } from 'appJS/models/Language';
import cx from 'classnames';
import { PreviewedTaskInfo } from './TaskDetailsDrawer';

const strings = translations[getLangKey()];

const sortConfig = (field: string, taskOrderBy?: UserTaskOrder) => {
  if (!taskOrderBy) {
    return {
      defaultSortOrder: sortTask.defaultSortOrder(field) as SortOrder
    };
  }

  let sortOrder: SortOrder = null;
  if (taskOrderBy[field] === OrderBy.ASC) sortOrder = 'ascend';
  if (taskOrderBy[field] === OrderBy.DESC) sortOrder = 'descend';
  return { sortOrder };
};

export const getUserTasksTableColumnsConfig = ({
  descriptionSearchValue,
  pageSearchValue,
  showSearchMatchContext,
  taskOrderBy
}: {
  descriptionSearchValue: string;
  pageSearchValue?: string;
  showSearchMatchContext?: boolean;
  taskOrderBy?: UserTaskOrder;
}) => [
  {
    title: strings.projectLabel,
    dataIndex: 'project.name',
    key: 'project.name',
    className: styles[classNames.column.project],
    render: render.projectName,
    width: 150,
    ellipsis: true,
    ...sortConfig('projectName', taskOrderBy),
    sorter: true
  },
  {
    title: strings.taskLabel,
    dataIndex: 'description',
    key: 'description',
    className: styles[classNames.column.task],
    render: render.description.bind(null, {
      query: descriptionSearchValue,
      pageQuery: pageSearchValue || '',
      showMatchContext: !!showSearchMatchContext
    }),
    ellipsis: !showSearchMatchContext
  },
  {
    title: strings.statusLabel,
    dataIndex: 'statusName',
    key: 'statusName',
    className: styles[classNames.column.status],
    render: render.status,
    width: 105,
    ellipsis: true,
    ...sortConfig('statusName', taskOrderBy),
    sorter: true
  },
  {
    title: strings.reportedByLabel,
    dataIndex: 'requesterName',
    key: 'reportedBy',
    className: styles[classNames.column.reportedBy],
    render: render.reportedBy,
    ...sortConfig('requesterName', taskOrderBy),
    sorter: true,
    width: 130,
    ellipsis: true
  },
  {
    title: strings.reportedLabel,
    dataIndex: 'createdAt',
    key: 'createdAt',
    className: styles[classNames.column.reported],
    render: render.reported,
    ...sortConfig('createdAt', taskOrderBy),
    sorter: true,
    width: 130
  },
  {
    title: strings.dueDateLabel,
    dataIndex: 'dueAt',
    key: 'dueAt',
    className: styles[classNames.column.dueDate],
    render: render.dueDate,
    width: 115,
    ...sortConfig('dueAt', taskOrderBy),
    sorter: true
  },
  {
    title: strings.updatedLabel,
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    className: styles[classNames.column.updatedAt],
    render: render.updatedAt,
    ...sortConfig('updatedAt', taskOrderBy),
    sorter: true,
    width: 155
  },
  {
    title: strings.assignedToLabel,
    dataIndex: 'assignedUsers',
    key: 'assignedUsers',
    className: styles[classNames.column.assignedTo],
    render: render.assignedUsers,
    width: 115
  }
];

export const onUserTasksTableRow = ({
  userTask,
  previewedTaskInfo,
  setPreviewedTaskInfo,
  taskDetailsDrawerEnabled
}: {
  userTask: UserTask;
  previewedTaskInfo: PreviewedTaskInfo;
  setPreviewedTaskInfo: (info: PreviewedTaskInfo) => void;
  taskDetailsDrawerEnabled: boolean;
}) => ({
  href: kanbanTaskPath({
    projectId: userTask.project.id,
    localTaskId: userTask.localTaskId
  }),
  className: cx(styles.tableRow, {
    [styles.tableRowSelected]:
      taskDetailsDrawerEnabled && previewedTaskInfo.id === userTask.id
  }),
  onClick: event => {
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      return window.open(
        kanbanTaskPath({
          projectId: userTask.project.id,
          localTaskId: userTask.localTaskId
        }),
        '_blank'
      );
    }

    // Without the experiment, keep the original behaviour of navigating
    // straight to the task's kanban view.
    if (!taskDetailsDrawerEnabled) {
      window.location.href = kanbanTaskPath({
        projectId: userTask.project.id,
        localTaskId: userTask.localTaskId
      });
      return;
    }

    event.preventDefault();
    setPreviewedTaskInfo({
      id: userTask.id,
      apiKey: userTask.project.apiKey
    });
  }
});
